import { Loader2, Trash2, RefreshCw } from 'lucide-preact'
import type { IntegrationConnectionViewDto } from '@/types/integrations'

interface IntegrationPanelFooterProps {
  managingConnection: (IntegrationConnectionViewDto & { IsArchived?: boolean }) | null
  activeProviderMeta: { id: string; title: string } | null
  creating: boolean
  updating: boolean
  archiving: boolean
  deleting: boolean
  resetting: boolean
  hasManageChanges: boolean
  onClose: () => void
  onDelete: () => void
  onArchiveToggle: () => void
  onReset: () => void
  onCreate: () => void
  onUpdate: () => void
}

export function IntegrationPanelFooter({
  managingConnection,
  activeProviderMeta,
  creating,
  updating,
  archiving,
  deleting,
  resetting,
  hasManageChanges,
  onClose,
  onDelete,
  onArchiveToggle,
  onReset,
  onCreate,
  onUpdate,
}: IntegrationPanelFooterProps) {
  const anyBusy = creating || updating || archiving || deleting || resetting

  return (
    <div className="px-6 py-4 border-t border-slate-200 dark:border-slate-700 flex items-center justify-between gap-4 bg-slate-50/60 dark:bg-slate-800/40">
      <div className="flex items-center gap-3">
        {managingConnection && (
          <>
            <button
              type="button"
              onClick={onDelete}
              disabled={archiving || updating || deleting || resetting}
              className="px-4 py-2 text-sm font-medium rounded-lg border border-red-300 dark:border-red-600 bg-white dark:bg-slate-800 text-red-600 dark:text-red-400 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-red-50 dark:hover:bg-red-900/30"
            >
              {deleting && <Loader2 className="w-4 h-4 animate-spin" />}
              <Trash2 className="w-4 h-4" /> Delete
            </button>
            <button
              type="button"
              onClick={onArchiveToggle}
              disabled={archiving || updating || deleting || resetting}
              className="px-4 py-2 text-sm font-medium rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-50 dark:hover:bg-slate-700"
            >
              {archiving && <Loader2 className="w-4 h-4 animate-spin" />}
              {managingConnection.IsArchived ? 'Unarchive' : 'Archive'}
            </button>
            {managingConnection.Status === 'Error' && (
              <button
                type="button"
                onClick={onReset}
                disabled={archiving || updating || deleting || resetting}
                className="px-4 py-2 text-sm font-medium rounded-lg border border-amber-300 dark:border-amber-600 bg-white dark:bg-slate-800 text-amber-600 dark:text-amber-400 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-amber-50 dark:hover:bg-amber-900/30"
              >
                {resetting && <Loader2 className="w-4 h-4 animate-spin" />}
                <RefreshCw className="w-4 h-4" /> Reset
              </button>
            )}
          </>
        )}
      </div>
      <div className="flex items-center gap-3 ml-auto">
        <button
          type="button"
          onClick={onClose}
          className="px-4 py-2 text-sm font-medium rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-600"
          disabled={anyBusy}
        >
          Close
        </button>
        {activeProviderMeta && (
          <button
            type="button"
            onClick={onCreate}
            className="px-5 py-2 text-sm font-semibold rounded-lg bg-primary-600 hover:bg-primary-700 text-white flex items-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
            disabled={anyBusy}
          >
            {creating && <Loader2 className="w-4 h-4 animate-spin" />} Create Connection
          </button>
        )}
        {managingConnection && (
          <button
            type="button"
            onClick={onUpdate}
            disabled={updating || archiving || deleting || resetting || !hasManageChanges}
            className={`px-5 py-2 text-sm font-semibold rounded-lg flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed ${hasManageChanges ? 'bg-primary-600 hover:bg-primary-700 text-white' : 'bg-slate-300 dark:bg-slate-600 text-slate-600 dark:text-slate-300'
              }`}
          >
            {updating && <Loader2 className="w-4 h-4 animate-spin" />} Save Changes
          </button>
        )}
      </div>
    </div>
  )
}
